import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Container, Heading, Text, VStack, SimpleGrid, Button } from '@chakra-ui/react';
import { smsHref } from './textOrderLinks';

const FEATURED = {
    '1': {
        shop: 'Eco Spin Round Rock Laundry',
        city: 'Round Rock',
        phone: '(512) 782-0428',
        sms: '+15127820428',
    },
    '2': {
        shop: 'Spin and Shine',
        city: 'Georgetown',
    },
};

function money(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

function pounds(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return Number.isInteger(n) ? `${n} lb` : `${n} lb`;
}

/**
 * Public membership story for the shop site.
 * Round Rock and Spin and Shine always get the explanation. Plan cards use
 * the shop's live plans, so a price is never written in by hand.
 */
export default function SiteMembership({ config }) {
    const laundryId = String(config?.laundryId || '');
    const featured = FEATURED[laundryId];
    const sc = config?.siteContent || {};
    const themeColor = sc.themeColor || 'blue';
    const [plans, setPlans] = useState(null);

    useEffect(() => {
        if (!laundryId) return;
        let live = true;
        const API = process.env.REACT_APP_AWS_API_URL;
        axios.get(`${API}/api/membership/plans`, { params: { laundryId } })
            .then((res) => { if (live) setPlans(res.data?.plans || []); })
            .catch(() => { if (live) setPlans([]); });
        return () => { live = false; };
    }, [laundryId]);

    const list = plans || [];
    if (!featured && list.length === 0) return null;

    const shop = featured?.shop || config?.laundryName || 'This laundry';
    const city = (featured?.city || sc.city || '').trim();
    const phone = sc.phone || featured?.phone || '';
    const place = city ? `${shop} in ${city}` : shop;

    return (
        <Box id="membership" py={{ base: 16, md: 20 }} bg="gray.50">
            <Container maxW="1200px">
                <VStack spacing={4} textAlign="center" mb={10}>
                    <Text fontSize="sm" fontWeight="600" color={`${themeColor}.500`} textTransform="uppercase" letterSpacing="wide">
                        Membership
                    </Text>
                    <Heading fontSize={{ base: '2xl', md: '4xl' }} color="gray.800">
                        A month of wash and fold, paid in pounds
                    </Heading>
                    <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="720px">
                        {place}. A membership is a set number of pounds you pay for once a month.
                        Drop off, or schedule a pickup, and the order uses those pounds first.
                        Pounds past the plan are billed at that plan&apos;s per-pound rate.
                    </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} maxW="960px" mx="auto" mb={10}>
                    <Box bg="white" borderRadius="2xl" p={6}>
                        <Text fontWeight="700" color="gray.800">One payment for the month</Text>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            The pounds are on the membership before the next bag is full.
                            The monthly price covers those pounds.
                        </Text>
                    </Box>
                    <Box bg="white" borderRadius="2xl" p={6}>
                        <Text fontWeight="700" color="gray.800">Wash and fold draws the pounds down</Text>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            Each order uses the pounds you have left. You can see the balance
                            before you send the next bag.
                        </Text>
                    </Box>
                    <Box bg="white" borderRadius="2xl" p={6}>
                        <Text fontWeight="700" color="gray.800">Cancel from your account</Text>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            Future charges stop. Pounds you already paid for stay usable
                            until the end of that paid period.
                        </Text>
                    </Box>
                </SimpleGrid>

                {list.length > 0 && (
                    <SimpleGrid columns={{ base: 1, md: Math.min(list.length, 3) }} spacing={6} maxW="960px" mx="auto" mb={10}>
                        {list.map((plan) => {
                            const id = plan.planId || plan.plan_id;
                            const bedding = Number(plan.beddingDiscountPct ?? plan.bedding_discount_pct) || 0;
                            return (
                                <Box key={id} bg="white" borderRadius="2xl" p={6} borderWidth="1px" borderColor="gray.200">
                                    <Text fontSize="lg" fontWeight="800" color="gray.800">{plan.name}</Text>
                                    <Text fontSize="3xl" fontWeight="800" color={`${themeColor}.700`} mt={1}>
                                        {money(plan.monthlyPrice ?? plan.monthly_price)}
                                        <Text as="span" fontSize="md" fontWeight="600" color="gray.500"> / month</Text>
                                    </Text>
                                    <Text mt={2} color="gray.700">{pounds(plan.allotmentLb ?? plan.allotment_lb)} included</Text>
                                    <Text fontSize="sm" color="gray.500" mt={1}>
                                        Extra pounds {money(plan.overageRate ?? plan.overage_rate)} per lb
                                    </Text>
                                    {bedding > 0 && (
                                        <Text fontSize="sm" color="purple.600" mt={1}>{bedding}% off bedding</Text>
                                    )}
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                )}

                <VStack spacing={3}>
                    {list.length > 0 ? (
                        <Button as="a" href={`/${laundryId}/user/join`} colorScheme={themeColor} size="lg" borderRadius="full" px={8}>
                            Start a membership
                        </Button>
                    ) : (
                        <Text color="gray.600" textAlign="center" maxW="560px">
                            Ask which plan fits the pounds you send in a month.
                            {phone ? ' Text ' : ''}
                            {phone && (
                                <Text as="a" href={smsHref(featured?.sms || phone, '')} fontWeight="700" color={`${themeColor}.700`} textDecoration="underline">
                                    {phone}
                                </Text>
                            )}
                            {phone ? ' and we will match a plan to your hamper.' : ''}
                        </Text>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}
